import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';

import { WORKSPACE_PLAN } from '@/entities/workspace/constants';
import {
  getMonthlyExportCapacityDialogContent,
  openConstraintAction,
} from '@/entities/workspace/util/capacity-constraint-ux';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { trackEvent } from '@/shared/track';
import { getInfluencerDiscoveryCapacityError } from '@/shared/utils';
import { showDialog } from '@/shared/utils/show-dialog';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';
import {
  exportDiscoveryCreatorList,
  type ExportDiscoveryCreatorListRequest,
} from '../api/export-discovery-creator-list';

export function useExportDiscoveryCreatorListWithFeedback() {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;

  return useCallback(
    async (
      listId: string,
      fileName: string,
      request: ExportDiscoveryCreatorListRequest,
      signal?: AbortSignal,
    ) => {
      try {
        await exportDiscoveryCreatorList(listId, fileName, request, signal);
      } catch (error) {
        const capacityError = await getInfluencerDiscoveryCapacityError(error);

        if (capacityError?.message) {
          const feedback = getMonthlyExportCapacityDialogContent(
            workspace?.plan ?? WORKSPACE_PLAN.TRIAL,
            workspaceId,
          );
          const action = feedback.action;

          showDialog({
            title: feedback.title,
            message: feedback.description,
            confirmLabel: action ? action.label : undefined,
            onConfirm: action
              ? () =>
                  openConstraintAction(action, {
                    navigateInternal: (href) => navigate({ to: href }),
                  })
              : undefined,
          });
          trackEvent('Saved_list', 'export_failed', {
            reason: 'monthly_export_capacity_exceeded',
          });
          return;
        }

        trackEvent('Saved_list', 'export_failed');
        await showErrorDialog({
          error,
          title: 'Failed to export creators',
        });
      }
    },
    [navigate, workspace?.plan, workspaceId],
  );
}
